import { useState } from "react";
import Reveal from "./Reveal";
import { placeholder } from "../../lib/constants";

export default function Portfolio({ items, sectionContent, uiStrings, sectionId }) {
  const ps = uiStrings?.portfolio_section || {};
  const eyebrow = ps.eyebrow || sectionContent?.eyebrow || placeholder("short");
  const headline = ps.headline || sectionContent?.headline || placeholder("medium");
  const description = ps.description || sectionContent?.description || "";
  const allLabel = uiStrings?.general?.all_categories || placeholder("short");
  const [active, setActive] = useState(allLabel);
  const noProjectsLabel = uiStrings?.general?.no_projects || placeholder("short");

  const published = items.filter(i => i.published === 1 || i.published === true);
  const categories = [allLabel, ...new Set(published.map(i => i.category).filter(Boolean))];

  const filtered = active === allLabel ? published : published.filter((i) => i.category === active);

  return (
    <section data-id="site-portfolio" id={sectionId} className="bg-offwhite py-28 md:py-40">
      <div data-id="portfolio-container" className="max-w-7xl mx-auto px-6 md:px-12">
        <div data-id="portfolio-header" className="grid lg:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow text-warmgray" data-id="portfolio-eyebrow">{eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]" data-id="portfolio-headline">
              {headline}
            </h2>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-6 lg:col-start-7 lg:pt-6">
            {description && (
              <p className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed font-light mb-6" data-id="portfolio-description">
                {description}
              </p>
            )}
            <div className="flex flex-wrap gap-3" data-id="portfolio-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  data-id={`portfolio-filter-${c.toLowerCase()}`}
                  onClick={() => setActive(c)}
                  className={`px-5 py-2 text-[11px] tracking-[0.28em] uppercase border transition-all duration-500 ${
                    active === c
                      ? "bg-softblack text-offwhite border-softblack"
                      : "bg-transparent text-softblack border-fendi hover:border-champagne hover:text-champagne"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14" data-id="portfolio-grid">
          {filtered.map((p, idx) => (
            <Reveal
              key={p.uid}
              delay={idx * 80}
              className={`group ${idx % 3 === 1 ? "lg:mt-16" : ""}`}
            >
              <div data-id={`portfolio-card-img-wrapper-${p.uid}`} className="relative overflow-hidden">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.title}
                    data-id={`portfolio-card-img-${p.uid}`}
                    className="w-full h-[260px] sm:h-[340px] md:h-[440px] object-cover transition-transform duration-[1600ms] group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div data-id={`portfolio-card-placeholder-${p.uid}`} className="w-full h-[260px] sm:h-[340px] md:h-[440px] bg-fendi/5 flex items-center justify-center">
                    <svg data-id={`portfolio-card-placeholder-icon-${p.uid}`} width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4c5b0" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </div>
                )}
                <div data-id={`portfolio-card-overlay-${p.uid}`} className="absolute inset-0 bg-softblack/0 group-hover:bg-softblack/15 transition-colors duration-700" />
              </div>
              <div data-id={`portfolio-card-info-${p.uid}`} className="mt-4 md:mt-6 flex items-baseline justify-between gap-3 md:gap-4">
                <h3 className="font-serif text-xl sm:text-2xl text-softblack leading-snug" data-id={`portfolio-card-title-${p.uid}`}>
                  {p.title}
                </h3>
                <span className="eyebrow text-warmgray shrink-0" data-id={`portfolio-card-category-${p.uid}`}>{p.category}</span>
              </div>
              {p.description && (
                <p className="text-warmgray text-xs sm:text-sm leading-relaxed mt-2 md:mt-3 font-light" data-id={`portfolio-card-desc-${p.uid}`}>
                  {p.description}
                </p>
              )}
            </Reveal>
          ))}
          {filtered.length === 0 && (
            <div data-id="portfolio-empty" className="col-span-full text-center py-20 text-warmgray font-light">
              {noProjectsLabel}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
